import { ROUTE_ACTION_PLAN, ROUTE_RISK_ANALYSIS } from '@/constants/routes';
import { getRoute } from '@/utils';
import { Divider, useBreakpointValue } from '@cmpsr/components';
import { useNavigate } from 'react-router-dom';
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import DashboardHeaderXAxis from './dashboard-header-xaxis';

type ChartSeriesDataPoint = {
  x: string;
  y: number;
};

export type ChartSeriesData = {
  data: ChartSeriesDataPoint[];
  name?: string;
};

type DashboardHeaderChartProps = {
  series: ChartSeriesData[];
};

const navigationMap = [{ route: ROUTE_RISK_ANALYSIS }, { route: ROUTE_RISK_ANALYSIS }, { route: ROUTE_ACTION_PLAN }];
const defaultNavigationRoute = ROUTE_RISK_ANALYSIS;

const barColors = [
  'var(--chakra-colors-alert-success-default)',
  'var(--chakra-colors-alert-error-default)',
  'var(--chakra-colors-tan)',
];

export default function DashboardHeaderChart(props: DashboardHeaderChartProps) {
  const navigate = useNavigate();
  const isMobile = useBreakpointValue({ base: true, md: false, lg: false }, { ssr: false }) || false;
  const isTablet = useBreakpointValue({ base: false, md: true, lg: false }, { ssr: false }) || false;

  const chartData =
    props.series && props.series.length > 0
      ? props.series[0].data.map((point, index) => ({
          category: point.x,
          value: point.y,
          originalIndex: index, // Keep originalIndex for color mapping and navigation
        }))
      : [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleBarClick = (data: any) => {
    if (data && typeof data.originalIndex === 'number') {
      const navInfo = navigationMap[data.originalIndex];
      if (navInfo) {
        navigate(getRoute(navInfo.route));
      } else {
        navigate(getRoute(defaultNavigationRoute));
      }
    }
  };

  return (
    <>
      <Divider borderColor="darkBlue" opacity={1} mb={3} display={{ base: 'none', md: 'block' }} />
      <ResponsiveContainer width="100%" height={160}>
        <BarChart
          data={chartData}
          margin={{
            top: 30,
            right: 0,
            left: 0,
            bottom: isMobile ? 40 : isTablet ? 30 : 30,
          }}
          barCategoryGap="35%"
        >
          <XAxis dataKey="category" hide={true} />
          <YAxis axisLine={false} tickLine={false} tick={false} width={0} />
          <Bar dataKey="value" radius={[4, 4, 4, 4]} onClick={handleBarClick} cursor="pointer">
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={barColors[entry.originalIndex % barColors.length]} />
            ))}
            <LabelList
              dataKey="value"
              position="top"
              offset={15}
              fill="var(--chakra-colors-tan)"
              fontSize={20}
              fontFamily="Inter"
              fontWeight="400"
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <DashboardHeaderXAxis />
    </>
  );
}
